<script setup lang="ts">
import BSubpageIcon from '@/components/BSubpageIcon.vue';
import { useStore } from '@/stores/main';
import { computed, inject, onMounted, ref, onBeforeUnmount, watch, nextTick } from 'vue';
import { IonSpinner } from '@ionic/vue';

const props = withDefaults(defineProps<{
  sections: any[];
  isWidget?: boolean;
}>(), {
  isWidget: false,
});

const store = useStore();
const colorScheme = inject('colorScheme', 'green');
const nextSlide = inject('nextSlide') as (() => void) | undefined;
const parentHeight = inject('parentHeight', null) as { value: number } | null;

// Refs for animations and UI state
const isLoaded = ref(false);
const activeSectionIndex = ref<number | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const hasNavigated = ref(false);
const isIOSDevice = ref(false);
const headerHeight = ref('80px');

// IMPORTANT: Consolidated button visibility control
// Using a single source of truth for button visibility
const navigationBarVisible = ref(true);

// Comprehensive device detection
function detectDevice() {
  // iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
         /iPhone|iPad|iPod/.test(navigator.platform) ||
         (navigator.userAgent.includes('Mac') && 'ontouchend' in document) ||
         window.navigator.userAgent.indexOf('iPad') > -1 ||
         window.navigator.userAgent.indexOf('iPhone') > -1;

  // Samsung/Android detection
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSamsung = /Samsung|SM-|GT-/.test(navigator.userAgent);

  isIOSDevice.value = isIOS;
  
  return {
    isIOS,
    isAndroid,
    isSamsung,
    isMobile: isIOS || isAndroid || window.innerWidth < 768,
    isTablet: (isIOS || isAndroid) && window.innerWidth >= 768 && window.innerWidth <= 1024
  };
}

// Detect if we're in a widget environment
const isInWidget = computed(() => {
  if (props.isWidget) return true;
  const inWidgetContainer = document.querySelector('.widget-container, .embedded-container') !== null;
  const isIOSWidgetContext = isIOSDevice.value && 
                            (window.navigator.userAgent.includes('Widget') || 
                             window.innerWidth < 400);
  return inWidgetContainer || isIOSWidgetContext;
});

// Background color based on color scheme
const backgroundColor = computed(() => {
  return {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  }[colorScheme];
});

// Accent color for text and elements
const accentColor = computed(() => {
  return {
    purple: 'purple-dark',
    yellow: 'yellow-darker',
    green: 'green-darker',
    blue: 'blue-darker',
  }[colorScheme];
});

// Navigation bar classes - simplified approach
const navigationBarClasses = computed(() => ({
  'navigation-bar': true,
  'widget-mode': isInWidget.value,
  'ios-device': isIOSDevice.value
}));

// Content padding - simplified computation
// const contentPadding = computed(() => {
//   const topPadding = isIOSDevice.value ? parseInt(headerHeight.value) + 20 + 'px' : '120px';
//   const bottomPadding = isIOSDevice.value ? '200px' : '120px';
//   return `${topPadding} 0 ${bottomPadding}`;
// });

// Simplified button visibility enforcement
function enforceButtonVisibility() {
  navigationBarVisible.value = true;
  
  nextTick(() => {
    const navBar = document.querySelector('.navigation-bar') as HTMLElement | null;
    if (navBar) {
      Object.assign(navBar.style, {
        display: 'flex',
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        visibility: 'visible',
        opacity: '1',
        transform: 'translateY(0)',
        webkitTransform: 'translateY(0)',
        zIndex: '2147483647', // Maximum possible z-index
        pointerEvents: 'auto'
      });
    }
    
    const startButton = document.querySelector('.start-button') as HTMLElement | null;
    if (startButton) {
      Object.assign(startButton.style, {
        visibility: 'visible',
        opacity: '1',
        pointerEvents: 'auto'
      });
    }
  });
}

// Handle start button click with optimized approach
function handleStartClick() {
  if (typeof nextSlide !== 'function') return;
  
  hasNavigated.value = true;
  enforceButtonVisibility();
  
  // Use setTimeout to ensure the click is processed first
  setTimeout(() => {
    nextSlide();
    
    // Re-enforce visibility after navigation
    setTimeout(() => {
      enforceButtonVisibility();
      hasNavigated.value = false;
    }, 100);
  }, 10);
}

// Get header height from DOM
function detectHeaderHeight() {
  const headerElement = document.querySelector('header.site-header, .app-header, .min-livsstil-header');
  if (headerElement) {
    headerHeight.value = `${headerElement.clientHeight}px`;
  } else if (isIOSDevice.value) {
    headerHeight.value = '120px';
  }
}

// Calculate and set content height
function adjustContentHeight() {
  if (!contentRef.value) return;
  
  const totalHeight = parentHeight?.value || window.innerHeight;
  const headerOffset = isIOSDevice.value ? parseInt(headerHeight.value) : 80;
  const buttonHeight = isIOSDevice.value ? 140 : 120;
  
  const maxHeight = isIOSDevice.value 
    ? totalHeight - headerOffset - buttonHeight - 100 
    : totalHeight - buttonHeight - 20;
  
  contentRef.value.style.maxHeight = `${Math.max(300, maxHeight)}px`;
  
  if (isIOSDevice.value) {
    Object.assign(contentRef.value.style, {
      height: `${Math.max(300, maxHeight)}px`,
      paddingTop: `${headerOffset + 20}px`,
      paddingBottom: '200px',
      transform: 'translateZ(0)',
      webkitTransform: 'translateZ(0)'
    });
  }
}

// Enhanced document-level fixes
function applyDocumentFixes() {
  if (isIOSDevice.value) {
    Object.assign(document.documentElement.style as any, {
      height: '-webkit-fill-available',
      overflowY: 'auto',
      webkitOverflowScrolling: 'touch'
    });
    
    Object.assign(document.body.style as any, {
      height: '-webkit-fill-available',
      paddingBottom: '200px',
      overflowY: 'auto',
      webkitOverflowScrolling: 'touch'
    });
    
    // Apply fix to Ionic components if present
    const ionContent = document.querySelector('ion-content');
    if (ionContent) {
      (ionContent as HTMLElement).style.height = '-webkit-fill-available';
    }
    
    const pageElement = document.querySelector('ion-page');
    if (pageElement) {
      (pageElement as HTMLElement).style.overflowY = 'auto';
      ((pageElement as HTMLElement).style as any).webkitOverflowScrolling = 'touch';
    }
    
    // Force overflow visibility on swiper components
    const swiperContainers = document.querySelectorAll('.swiper-container');
    swiperContainers.forEach(container => {
      (container as HTMLElement).style.overflow = 'visible';
    });
    
    const swiperWrappers = document.querySelectorAll('.swiper-wrapper');
    swiperWrappers.forEach(wrapper => {
      (wrapper as HTMLElement).style.height = 'auto';
      (wrapper as HTMLElement).style.minHeight = '60vh';
    });
  }
}

// Debounced resize handler
let resizeTimer: ReturnType<typeof setTimeout> | undefined;
function handleResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    adjustContentHeight();
    enforceButtonVisibility();
  }, 100);
}

// Section interaction functions
function highlightSection(index: number) {
  activeSectionIndex.value = index;
}

function resetHighlight() {
  activeSectionIndex.value = null;
}

// CRITICAL: Create a MutationObserver to monitor DOM changes that might affect button visibility
let buttonVisibilityObserver: MutationObserver | undefined;
function setupButtonVisibilityObserver() {
  if (typeof MutationObserver === 'undefined') return;
  
  // If a previous observer exists, disconnect it
  if (buttonVisibilityObserver) buttonVisibilityObserver.disconnect();
  
  buttonVisibilityObserver = new MutationObserver((mutations) => {
    enforceButtonVisibility();
  });
  
  // Start observing document body for DOM changes
  buttonVisibilityObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });
}

// Visibility state monitoring
function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    enforceButtonVisibility();
  }
}

// Set up all initial state and listeners
onMounted(async () => {
  // Refresh profile from server to get latest state (handles cross-browser sync)
  const hasActiveProfile = await store.refreshActiveProfile();
  if (!hasActiveProfile) {
    // No active profile - redirect to home
    window.location.href = '/home';
    return;
  }

  // Detect device type
  const deviceInfo = detectDevice();
  
  // Detect header height
  detectHeaderHeight();
  
  // Check for save points
  if (
    store.savePoints && 
    store.savePoints.life && 
    store.savePoints.life.page !== null &&
    store.savePoints.life.page !== 0
  ) {
    nextTick(() => {
      if (typeof nextSlide === 'function') {
        nextSlide();
      }
    });
  }
  
  // Animate content in
  setTimeout(() => {
    isLoaded.value = true;
  }, 100);
  
  // Initial layout adjustments
  await nextTick();
  adjustContentHeight();
  enforceButtonVisibility();
  applyDocumentFixes();
  
  // Set up button visibility observer
  setupButtonVisibilityObserver();
  
  // Set up multiple ways to enforce button visibility
  window.addEventListener('resize', handleResize, {passive: true});
  window.addEventListener('scroll', enforceButtonVisibility, {passive: true});
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      adjustContentHeight();
      enforceButtonVisibility();
    }, 300);
  });
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Poll for visibility as a last resort
  const visibilityInterval = setInterval(enforceButtonVisibility, 500);
  
  // Clean up the interval on unmount
  onBeforeUnmount(() => {
    clearInterval(visibilityInterval);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', enforceButtonVisibility);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    if (buttonVisibilityObserver) {
      buttonVisibilityObserver.disconnect();
    }
  });
});
</script>

<template>
  <div class="lifestyle-screen " :class="[backgroundColor, { 'loaded': isLoaded, 'widget-mode': isInWidget, 'ios-device': isIOSDevice }]">
    <!-- Decorative Elements -->
    <div class="decorative-element element-1"></div>
    <div class="decorative-element element-2"></div>
    
    <!-- Main Content Area -->
    <div class="content-container"  :style="{ paddingTop: '100px', paddingBottom: '100px'  }">
      <div class="content-wrapper">
        <!-- Header Section -->
        <header class="header-section">
          <h1 class="main-title">Levnadsvanor</h1>
          <p class="subtitle">Här ser du vad det handlar om.</p>
        </header>
        
        <!-- Sections Grid -->
        <div class="sections-container">
          <div v-if="sections.length === 0" class="loading-state">
            <ion-spinner />
            <span :class="`text-${accentColor}`">Laddar områden...</span>
          </div>
          
          <ul v-else class="sections-grid">
            <li 
              v-for="(section, index) in sections" 
              :key="index"
              class="section-item"
              :class="{ 'active': activeSectionIndex === index }"
              @mouseenter="highlightSection(index)"
              @mouseleave="resetHighlight()"
              :style="{ '--delay': `${0.2 + index * 0.1}s` }"
            >
              <div class="section-icon">
                <b-subpage-icon :icon="section.pageName" />
              </div>
              <span class="section-label" v-html="section.pageLabel"></span>
            </li>
            <li> <!-- Instruction Text -->
        <p class="instruction-text">
          Du kan hoppa mellan de olika delarna när du klickat på START.
        </p></li>
          </ul>
        </div>
        
       
        
        <!-- Increased button spacer for reliable spacing -->
        <div class="button-spacer" :class="{'widget-spacer': isInWidget, 'ios-spacer': isIOSDevice}"></div>
      </div>
    </div>
    
    <!-- Fixed Navigation Bar - Now using a more direct approach -->
    <!-- <div v-if="navigationBarVisible" :class="navigationBarClasses">
      <div class="navigation-content">
        <button 
          class="start-button" 
          :class="[`start-button-${colorScheme}`, {'widget-button': isInWidget, 'ios-button': isIOSDevice}]"
          @click="handleStartClick"
        >
          <span class="button-text">Starta!</span>
          <svg class="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"></path>
            <path d="m12 5 7 7-7 7"></path>
          </svg>
        </button>
      </div>
    </div> -->
  </div>
</template>

<style>
/* Global styles to ensure the button is always shown */
.navigation-bar {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 2147483647 !important;
  display: flex !important;
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
  transform: translateY(0) !important;
  -webkit-transform: translateY(0) !important;
}

.start-button {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
}
</style>

<style scoped>
/* Base Layout */
.lifestyle-screen {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.lifestyle-screen.loaded {
  opacity: 1;
  transform: translateY(0);
}

.lifestyle-screen.widget-mode {
  min-height: auto;
  opacity: 1;
  transform: none;
}

/* iOS Optimized Layout */
.lifestyle-screen.ios-device {
  /* padding-bottom: 200px; */
  min-height: calc(100vh - 120px);
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  -webkit-overflow-scrolling: touch;
}

/* Decorative Elements */
.decorative-element {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  pointer-events: none;
  filter: blur(40px);
  z-index: 0;
}

.element-1 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.1));
  top: -100px;
  right: -50px;
}

.element-2 {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.05));
  bottom: 150px;
  left: -100px;
}

/* Content Container */
.content-container {
  width: 100%;
  flex: 1;
  max-height: 100svh;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 2;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  position: relative;
  min-height: 300px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.content-container::-webkit-scrollbar {
  display: none;
}

.ios-device .content-container {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 200px !important;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  min-height: 400px;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Header Section */
.header-section {
  margin-bottom: 1rem;
}

.main-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #171717;
  margin: 0 0 0.75rem;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s ease forwards;
  animation-delay: 0.1s;
}

.subtitle {
  font-size: 1.125rem;
  color: #171717;
  margin: 0;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s ease forwards;
  animation-delay: 0.2s;
}

/* Sections Container */
.sections-container {
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s ease forwards;
  animation-delay: 0.3s;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  font-weight: 500;
}

.sections-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.section-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s ease forwards;
  animation-delay: var(--delay, 0.2s);
}

.ios-device .section-item {
  -webkit-tap-highlight-color: rgba(0,0,0,0);
  touch-action: manipulation;
}

.section-item:hover, .section-item.active {
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
}

.section-label {
  font-weight: 500;
  color: var(--green-darker, #2e7d32);
}

/* Instruction Text */
.instruction-text {
  font-size: 1.125rem;
  color: #171717;
  margin: 0;
  line-height: 1.5;

  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s ease forwards;
  animation-delay: 0.5s;
}

/* Extra-large button spacer */


.widget-spacer {
  height: 180px; /* Increased from 150px */
}

.ios-spacer {
  height: 250px !important; /* Increased from 220px */
}

/* Navigation Bar - Simplified and consolidated */
.navigation-bar {
  position: fixed !important;
  bottom: 20px !important; /* Added space from bottom for desktop */
  left: 0 !important;
  right: 0 !important;
  background-color: rgba(249, 252, 242, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.2);
  z-index: 2147483647 !important; /* Max possible z-index */
  padding: 1rem;
  margin: 0 auto; /* Center the navigation bar */
  max-width: 90%; /* Limit width on larger screens */
  width: 600px; /* Set a standard width */
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  display: flex !important;
  opacity: 1 !important;
  visibility: visible !important;
  transform: translateY(0) !important;
  -webkit-transform: translateY(0) !important;
  will-change: transform;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transform: translateZ(0) !important;
  -webkit-transform: translateZ(0) !important;
  pointer-events: auto !important;
}

.ios-device .navigation-bar {
  bottom: 0 !important; /* Override the desktop spacing for iOS */
  width: 100% !important; /* Full width for iOS */
  max-width: 100% !important; /* Ensure full width */
  border-radius: 0 !important; /* No border radius on iOS */
  padding-bottom: calc(2rem + env(safe-area-inset-bottom, 20px)) !important;
}

.navigation-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 0.5rem 0;
  width: 100%;
}

/* Start button - Unified and simplified */
.start-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 50px;
  border: none;
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
  color: white;
  cursor: pointer;
  position: relative;
  z-index: 2147483647; /* Max possible z-index */
  transform: translateZ(0) !important;
  -webkit-transform: translateZ(0) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  /* Added visibility enforcers */
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
}

.ios-button {
  -webkit-appearance: none;
  appearance: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
  padding: 1.5rem 2rem !important;
  margin-bottom: env(safe-area-inset-bottom, 0px);
  outline: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  touch-action: manipulation;
}

.widget-button {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  animation: none !important;
}

.start-button-green {
  background-color: var(--green-darker, #2e7d32);
}

.start-button-purple {
  background-color: var(--purple-dark, #7b1fa2);
}

.start-button-yellow {
  background-color: var(--yellow-darker, #f57f17);
}

.start-button-blue {
  background-color: var(--blue-darker, #1565c0);
}

.button-text {
  display: inline-block;
  transition: transform 0.3s ease;
}

.button-icon {
  width: 1.25rem;
  height: 1.25rem;
  transition: transform 0.3s ease;
}

.start-button:hover .button-text {
  transform: translateX(-5px);
}

.start-button:hover .button-icon {
  transform: translateX(5px);
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(46, 125, 50, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0);
  }
}

/* Responsive Layout Adjustments */
@media (min-width: 640px) {
  .main-title {
    font-size: 2rem;
  }
  
  .subtitle, .instruction-text {
    font-size: 1.25rem;
  }
  
  .sections-grid {
    gap: 1.25rem;
  }
  
  .navigation-bar {
    padding: 1.25rem;
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }
}

@media (min-width: 768px) {
  .sections-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  .content-wrapper {
    padding: 0 2rem;
  }
  
  .main-title {
    font-size: 2.25rem;
  }
  
  .instruction-text {
    /* max-width: 60%; */
  }
}

@media (min-width: 1024px) {
  .main-title {
    font-size: 2.5rem;
    font-weight: 600;
  }
  
  .subtitle {
    font-size: 1.5rem;
  }
  
  .instruction-text {
    font-size: 1.25rem;
    /* max-width: 50%; */
  }
  
  .section-item {
    padding: 1.25rem;
  }
  
  .section-label {
    font-size: 1.125rem;
  }
}

@media (min-width: 1280px) {
  .content-wrapper {
    padding: 0 3rem;
  }
  
  .instruction-text {
    /* max-width: 40%; */
  }
}

/* Small Screen Optimizations */
@media (max-height: 700px) {
  .content-container {
    padding-top: 90px;
   
  }
  
  .main-title {
    font-size: 1.5rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .section-item {
    padding: 0.75rem;
  }
}

/* Landscape Mode Optimizations */
@media (orientation: landscape) and (max-height: 600px) {
  .content-container {
    padding: 80px 0 150px;
  }
  
  .navigation-bar {
    padding: 0.75rem;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }
}

/* Parent containing Ionic components fixes */
:deep(ion-content) {
  --overflow: auto !important;
}

:deep(ion-page) {
  overflow-y: auto !important;
}

:deep(.swiper) {
  overflow-y: auto !important;
  height: auto !important;
}

/* iOS-specific scrolling fixes */
@supports (-webkit-touch-callout: none) {
  .lifestyle-screen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    height: 100%;
    padding-bottom: 200px !important;
  }
  
  .content-container {
    padding-top: var(--header-height, 120px) !important;
    padding-bottom: 220px !important;
    min-height: 60vh !important;
  }
  
  .button-spacer {
    height: 220px !important;
  }
}
</style>